import React, { useState, useMemo, useEffect } from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import { map, find, filter, isEmpty, includes, isArray } from 'lodash';

const AutoComplete = ({
  data,
  showKey,
  multiple,
  selected,
  isDisabled,
  customStyle,
  doTranslate,
  placeholder,
  selectedKey,
  handleSelected,
  onOpen,
  hasError = false,
  helperText = '',
  onInputChange = () => {},
  ...props
}) => {
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState([]);

  const selectedData = useMemo(() => {
    if (selectedKey) {
      const item = find(data, [selectedKey, selected]);

      return item;
    }
    return null;
  }, [data, selected, selectedKey]);

  useEffect(() => {
    if (!isEmpty(data)) {
      const selectedKeys =
        selectedData ||
        (multiple
          ? map(selected, (item) => item[showKey])
          : selected
          ? selected[showKey]
          : '');
      let filteredItems = [];
      if (multiple) {
        filteredItems = filter(
          data,
          (item) => !includes(selectedKeys, item[showKey]),
        );
      } else {
        filteredItems = filter(data, (item) => selectedKeys !== item[showKey]);
      }
      setFilteredData(filteredItems);
    }
  }, [data, showKey, selected, multiple, selectedData]);

  return (
    <Autocomplete
      fullWidth
      onOpen={onOpen}
      id={placeholder}
      value={selectedData || selected}
      multiple={multiple}
      style={customStyle}
      options={filteredData}
      getOptionLabel={(option) =>
        !isEmpty(option)
          ? doTranslate
            ? t(option[showKey])
            : option[showKey]
          : ''
      }
      disabled={isDisabled}
      onChange={async (e, v) => {
        handleSelected(v);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          onInput={(e) => {
            onInputChange(e.target.value);
          }}
          error={hasError}
          helperText={helperText}
          variant="outlined"
          label={t(placeholder)}
          InputLabelProps={multiple ? {} : { shrink: true }}
          placeholder={
            (isArray(selected) && selected.length) || multiple
              ? ''
              : t(placeholder)
          }
        />
      )}
      {...props}
    />
  );
};

AutoComplete.defaultProps = {
  data: [],
  selected: [],
  multiple: true,
  customStyle: {},
  isDisabled: false,
  doTranslate: false,
};

AutoComplete.propTypes = {
  data: PropTypes.array,
  multiple: PropTypes.bool,
  isDisabled: PropTypes.bool,
  doTranslate: PropTypes.bool,
  selectedKey: PropTypes.string,
  customStyle: PropTypes.object,
  showKey: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  handleSelected: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  selected: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string,
  ]),
  hasError: PropTypes.bool,
  helperText: PropTypes.string,
};

export default AutoComplete;
